import React, { useEffect, useState } from "react";
import { BsPlusLg, BsTrash } from "react-icons/bs";
import { Dropdown } from "react-bootstrap";
// eslint-disable-next-line import/no-extraneous-dependencies
import json5 from "json5";
import { ReplacementGroup } from "../replacement-group";
import {
    AsyncObjectType,
    FieldTypedValueInput, FormFieldOptionSettings, FormFieldReplacementInput, FormFieldType,
    WorkflowFormField,
    WorkflowFormFieldArray,
    WorkflowFormFieldMap,
    WorkflowFormFieldRecord,
    WorkflowFormFieldSchema,
    WorkflowJsonSchemaType,
    WorkflowStepKeyValueParameterInput,
} from "../../__generated__/api";
import EditableControl, { FORM_FIELD, RecursiveOption, ValueTypes } from "./EditableText";
import createId from "../../create-id";
import { isDeepEqual } from "../../api";
import { HtmlRenderer } from "../html-render/HtmlRenderer";
import { useTheme } from "../../contexts/Theme";

export interface JsonProperties {
    value: string | Record<string, unknown>;
    input?: ReplacementGroup[];
    replacements?: FormFieldReplacementInput[];
    schema?: WorkflowFormFieldSchema;
    onChange: (json: string) => void;
    integrationId?: string;

}

export interface JsonValue {
    id: string;
    value?: WorkflowStepKeyValueParameterInput;
}

function tryParse(value: string | Record<string, unknown>): Record<string, unknown> | unknown[] | undefined {
    if (typeof value === "string") {
        try {
            return json5.parse(value || "[]");
        } catch {
            try {
                return JSON.parse(`{${value}}` || "[]");
            } catch {
                return json5.parse(`{${value.replace(/\n/g, ",\n")}}` || "[]");
            }
        }
    }
    return value;
}

export interface ParsedValue {
    id: string;
    value: WorkflowStepKeyValueParameterInput
}

export function tryParseValue(
    value: string | Record<string, unknown>,
    replacements?: FormFieldReplacementInput[],
): ParsedValue[] {
    const list = tryParse(value);
    if (Array.isArray(list)) {
        return (list as WorkflowStepKeyValueParameterInput[]).map(row => ({
            id: createId(),
            value: row,
        }));
    } if (typeof list === "object") {
        return Object.entries(list).map(([key, objectValue]) => {
            let type = FormFieldType.Text;
            if (typeof objectValue === "number") {
                type = FormFieldType.Number;
            } else if (typeof objectValue === "boolean") {
                type = FormFieldType.Boolean;
            }
            return {
                id: createId(),
                value: {
                    key,
                    replacement: replacements,
                    value: {
                        fieldId: "urn:fq:form-field:none",
                        type,
                        string: objectValue as string,
                    },
                } as WorkflowStepKeyValueParameterInput,
            };
        });
    }
    throw new Error("Invalid JSON");
}

export function isArray(item?: unknown): item is WorkflowFormFieldArray {
    return (item as { __typename: string })?.__typename === "WorkflowFormFieldArray";
}

export function isMap(item?: unknown): item is WorkflowFormFieldMap {
    return (item as { __typename: string })?.__typename === "WorkflowFormFieldMap";
}

export function isRecord(item?: unknown): item is WorkflowFormFieldRecord {
    return (item as { __typename: string })?.__typename === "WorkflowFormFieldRecord";
}

export function isSchema(item?: unknown): item is WorkflowFormFieldSchema {
    return (item as { __typename: string })?.__typename === "WorkflowFormFieldSchema";
}

export function isFormField(item?: unknown): item is WorkflowFormField {
    return (item as { __typename: string })?.__typename === "WorkflowFormField";
}

export default function JsonBuilder({
    value, onChange, input, replacements, schema,
    integrationId,
}: JsonProperties) {
    const theme = useTheme();

    const [json, setJson] = useState<JsonValue[]>(() => tryParseValue(value, replacements));
    const [next, setNext] = useState(() => Object.keys(json).filter(key => key.startsWith("Untitled")).length + 1);
    const onAdded = (type: FormFieldType | WorkflowJsonSchemaType, fieldId?: string) => {
        let valueType = type;
        if (type === WorkflowJsonSchemaType.Array && isArray(schema?.root)) {
            valueType = schema.root.items.type as FormFieldType;
        } else if (type === WorkflowJsonSchemaType.Map && isMap(schema?.root)) {
            valueType = schema.root.values.type as FormFieldType;
        } else if (type === WorkflowJsonSchemaType.Record && isRecord(schema?.root)) {
            // valueType = schema.root.fields. as FormFieldType;
        } else if (type === WorkflowJsonSchemaType.Schema && isSchema(schema?.root)) {
            valueType = schema.root.root.type as FormFieldType;
        }

        setNext(next + 1);
        setJson([
            ...json,
            {
                id: createId(),
                value: {
                    key: fieldId || `Untitled_${next}`,
                    value: {
                        fieldId: "urn:fq:form-field:none",
                        type: valueType as FormFieldType,
                    },
                },
            },
        ]);
    };
    const onRemove = (id: string) => {
        const copy = [...json.filter(row => row.id !== id)];
        setJson(copy);
    };

    useEffect(() => {
        onChange(JSON.stringify(json.map(row => row.value)));
    }, [json]);

    useEffect(() => {
        const jsonValue = tryParseValue(value, replacements);
        if (!isDeepEqual(json.toIdList("value"), jsonValue.toIdList("value"))) {
            setJson(jsonValue);
        }
    }, [value]);

    const keys = isMap(schema?.root) && isFormField(schema.root.keys) ? schema.root.keys : undefined;
    const values = isMap(schema?.root) && isFormField(schema.root.values) ? schema.root.values : undefined;

    const itemTypes = isRecord(schema?.root) ? schema.root.fields?.toIdMap("id") : undefined;

    let menuItems;
    if (isMap(schema?.root)) {
        menuItems = (
            <Dropdown.Item onClick={() => onAdded(schema.root.type as FormFieldType)}>
                Add
                {" "}
                {schema?.root.label}
            </Dropdown.Item>
        );
    } else if (isRecord(schema?.root)) {
        menuItems = schema.root.fields.map(field => (
            <Dropdown.Item key={field.id} onClick={() => onAdded(field.type as FormFieldType, field.id)}>
                Add
                {" "}
                {field.name}
            </Dropdown.Item>
        ));
    } else {
        menuItems = (
            <>
                <Dropdown.Item onClick={() => onAdded(FormFieldType.Text)}>
                    Text
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAdded(FormFieldType.LongText)}>
                    Long Text
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAdded(FormFieldType.Number)}>
                    Number
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAdded(FormFieldType.Boolean)}>
                    Boolean
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAdded(FormFieldType.Date)}>
                    Date
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAdded(FormFieldType.DateTime)}>
                    Date/Time
                </Dropdown.Item>
            </>
        );
    }
    return (
        <div>

            <Dropdown className="p-0">
                <Dropdown.Toggle
                    variant=""
                    className="btn-none btn text-gray-500 ms-0 px-0"
                >
                    <span className="px-1 rounded-pill hover-shadow">
                        <BsPlusLg size="15" />
                    </span>
                    {" "}
                    Add Value
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {menuItems}
                </Dropdown.Menu>
            </Dropdown>
            <div className="list-group">
                {json.map(child => {
                    const itemType = itemTypes?.[child.value?.key as string];
                    const valueType = itemType ?? values;
                    let keyComponent;
                    if (itemType) {
                        keyComponent = (
                            <div>
                                <h4>{itemType.name}</h4>
                                <HtmlRenderer html={itemType.description as string} />
                            </div>
                        );
                    } else if (isMap(schema?.root) && keys) {
                        keyComponent = (
                            <EditableControl
                                inputClassName="form-control py-1"
                                placeholderClassName="text-muted py-0"
                                variableClassName={theme.isV3 ? "text-muted py-0" : "p-2 ms-2 my-0"}
                                placeholder="Value"
                                replaceableInput={input}
                                isFormField
                                options={keys?.options as RecursiveOption<unknown>[]}
                                labels={keys?.levels as string[]}
                                asyncObjectType={keys?.asyncObjectType as AsyncObjectType}
                                optionsSettings={keys?.optionsSettings as FormFieldOptionSettings}
                                integrationId={integrationId}
                                shouldFocus={false}
                                jsonSchema={keys?.schema as WorkflowFormFieldSchema}
                                type={FORM_FIELD[schema?.root.keys.type as FormFieldType ?? FormFieldType.Text]}
                                replacements={child?.value?.replacement as FormFieldReplacementInput[] ?? []}
                                value={child?.value?.key as ValueTypes}
                                isEditing
                                onChange={newValue => {
                                    const newJson = [...json];
                                    const rowValue = newJson.find(row => row === child) ?? {} as JsonValue;
                                    rowValue.value ??= {} as WorkflowStepKeyValueParameterInput;
                                    rowValue.value.key = newValue as string;
                                    setJson(newJson);
                                }}
                            />
                        );
                    } else {
                        keyComponent = (
                            <input
                                type="text"
                                className="form-control h-100 p-0"
                                placeholder="Key"
                                value={child.value?.key ?? ""}
                                onChange={event => {
                                    const newJson = [...json];
                                    const rowValue = newJson.find(row => row === child) ?? {} as JsonValue;
                                    rowValue.value ??= {} as WorkflowStepKeyValueParameterInput;
                                    rowValue.value.key = event.target.value;
                                    setJson(newJson);
                                }}
                            />
                        );
                    }
                    return (
                        <div className="list-group-item" key={child.id}>
                            <div className="row">
                                <div className="col-4 ps-0">
                                    {keyComponent}
                                </div>
                                <div className="col px-0 d-flex">
                                    <EditableControl
                                        inputClassName="form-control py-1"
                                        placeholderClassName="text-muted py-0"
                                        variableClassName={theme.isV3 ? "text-muted py-0" : "p-2 ms-2 my-0"}
                                        placeholder="Value"
                                        replaceableInput={input}
                                        isFormField
                                        shouldFocus={false}
                                        options={valueType?.options as RecursiveOption<unknown>[]}
                                        labels={valueType?.levels as string[]}
                                        asyncObjectType={valueType?.asyncObjectType as AsyncObjectType}
                                        optionsSettings={valueType?.optionsSettings as FormFieldOptionSettings}
                                        integrationId={integrationId}
                                        jsonSchema={valueType?.schema as WorkflowFormFieldSchema}
                                        type={FORM_FIELD[valueType?.type ?? FormFieldType.Text]}
                                        replacements={child?.value?.replacement as FormFieldReplacementInput[] ?? []}
                                        value={child?.value?.value.string as ValueTypes}
                                        isEditing
                                        onChange={(newValue, replace) => {
                                            const newJson = [...json];
                                            const rowValue = newJson.find(row => row === child) ?? {} as JsonValue;
                                            rowValue.value ??= {} as WorkflowStepKeyValueParameterInput;
                                            rowValue.value.key = itemType?.id;
                                            rowValue.value.value ??= {} as FieldTypedValueInput;
                                            rowValue.value.replacement = replace;
                                            if (typeof newValue === "string") {
                                                rowValue.value.value.string = newValue;
                                            } else if (typeof newValue === "number") {
                                                rowValue.value.value.number = newValue;
                                            } else if (typeof newValue === "boolean") {
                                                rowValue.value.value.boolean = newValue;
                                            } else if (newValue instanceof Date) {
                                                rowValue.value.value.date = newValue;
                                            }
                                            setJson(newJson);
                                        }}
                                    />
                                    <button
                                        className="text-light btn btn-none"
                                        type="button"
                                        onClick={() => onRemove(child.id)}
                                        aria-label="Delete value"
                                    >
                                        <BsTrash />
                                    </button>

                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

JsonBuilder.defaultProps = {
    input: undefined,
    replacements: undefined,
    schema: undefined,
    integrationId: undefined,
};
